import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type SubscriberType = 'LOCUM' | 'COMPANY';

export interface SubscriptionPlan {
  id: string;
  code: string;
  name: string;
  subscriberType: 'LOCUM' | 'COMPANY';
  billingInterval: 'MONTHLY' | 'ANNUAL';
  amount: number;
  currency: string;
  isActive: boolean;
  jobPostLimit: number;
  jobApplicationLimit: number;
  jobAlertAccess: boolean;
  jobInviteAccess: boolean;
  findLocumAccess: boolean;
  features: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface Subscription {
  id: string;
  subscriberType: 'LOCUM' | 'COMPANY';
  userId?: string;
  companyId?: string;
  plan: SubscriptionPlan;
  provider: string;
  providerCustomerCode?: string;
  providerSubscriptionCode?: string;
  status: 'FREE' | 'PENDING_PAYMENT' | 'ACTIVE' | 'PAST_DUE' | 'GRACE_PERIOD' | 'EXPIRED' | 'CANCELED';
  startsAt: string | null;
  expiresAt: string | null;
  autoRenewEnabled: boolean;
  canceledAt?: string;
  gracePeriodEndsAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BillingHistoryItem {
  id: string;
  planName: string;
  amount: number;
  currency: string;
  status: string;
  billingInterval: string;
  paidAt?: string;
  createdAt: string;
}

export interface SubscriptionEntitlements {
  planCode?: string | null;
  allowanceResetsAt?: string;
  usage?: {
    jobPosts: { used: number; limit: number | null; remaining: number | null };
    jobApplications: { used: number; limit: number | null; remaining: number | null };
  };
  canApplyToJobs: boolean;
  canPostJobs: boolean;
  canReceiveJobAlerts: boolean;
  canReceiveJobInvites: boolean;
  canUseFindLocum: boolean;
  monthlyJobPostLimit: number | null;
  monthlyJobApplicationLimit: number | null;
}

export interface CheckoutSession {
  authorizationUrl: string;
  accessCode: string;
  reference: string;
  publicKey: string;
}

interface SubscriptionState {
  plans: SubscriptionPlan[];
  activeSubscription: Subscription | null;
  entitlements: SubscriptionEntitlements | null;
  billingHistory: BillingHistoryItem[];
  checkoutSession: CheckoutSession | null;
  loading: boolean;
  error: string | null;
  subscriptionError: string | null;
  entitlementsError: string | null;
}

const initialState: SubscriptionState = {
  plans: [],
  activeSubscription: null,
  entitlements: null,
  billingHistory: [],
  checkoutSession: null,
  loading: false,
  error: null,
  subscriptionError: null,
  entitlementsError: null,
};

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    setPlans(state, action: PayloadAction<SubscriptionPlan[]>) {
      state.plans = action.payload;
      state.error = null;
    },
    setActiveSubscription(state, action: PayloadAction<Subscription | null>) {
      state.activeSubscription = action.payload;
      state.error = null;
      state.subscriptionError = null;
    },
    setEntitlements(state, action: PayloadAction<SubscriptionEntitlements | null>) {
      state.entitlements = action.payload;
      state.entitlementsError = null;
    },
    setSubscriptionError(state, action: PayloadAction<string | null>) {
      state.subscriptionError = action.payload;
    },
    setEntitlementsError(state, action: PayloadAction<string | null>) {
      state.entitlementsError = action.payload;
    },
    setBillingHistory(state, action: PayloadAction<BillingHistoryItem[]>) {
      state.billingHistory = action.payload;
    },
    setCheckoutSession(state, action: PayloadAction<CheckoutSession | null>) {
      state.checkoutSession = action.payload;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    resetSubscriptionState: () => initialState,
  },
});

export const subscriptionActions = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
